import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SectionEditor from '@/components/SectionEditor';
import { Leaf, Recycle, TreePine, Droplets, Wind, Sun } from 'lucide-react';
import { applyStyle } from '@/utils/styleHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultHero = { title: 'Sustainability', subtitle: 'Our commitment to environmental responsibility', backgroundImage: 'https://images.pexels.com/photos/12725408/pexels-photo-12725408.jpeg' };

export default function SustainabilityPage({ user, setUser, theme, toggleTheme, accentColor, onAccentChange }) {
  const [content, setContent] = useState({ hero: defaultHero });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/pages/sustainability`);
        if (r.data?.content) setContent(prev => ({ ...prev, ...r.data.content }));
      } catch (error) { console.error('Failed to load sustainability content:', error); }
    })();
  }, []);

  const updateSection = (key) => (data) => setContent(prev => ({ ...prev, [key]: data }));

  const initiatives = [
    { icon: Recycle, title: '100% Recyclable Materials', description: 'All our corrugated boxes are made from 100% recyclable materials, contributing to a circular economy.', stat: '100%' },
    { icon: TreePine, title: 'Sustainable Sourcing', description: 'We source our raw materials from FSC-certified forests, ensuring responsible forest management.', stat: '85%' },
    { icon: Droplets, title: 'Water Conservation', description: 'Advanced water recycling systems reduce our water consumption by 60% compared to industry standards.', stat: '60%' },
    { icon: Wind, title: 'Carbon Neutral Operations', description: 'Our manufacturing facilities are powered by renewable energy sources, achieving carbon neutrality.', stat: 'Net Zero' },
    { icon: Sun, title: 'Solar Energy', description: 'Solar panels installed across our facilities generate 40% of our total energy requirements.', stat: '40%' },
    { icon: Leaf, title: 'Zero Waste to Landfill', description: 'We recycle or repurpose 99% of our production waste, minimizing environmental impact.', stat: '99%' },
  ];

  const commitments = [
    'Reduce carbon emissions by 50% by 2030',
    'Achieve 100% renewable energy by 2028',
    'Plant 10,000 trees annually',
    'Partner with eco-conscious suppliers only',
    'Continuous innovation in sustainable materials',
    'Employee sustainability training programs',
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar user={user} setUser={setUser} theme={theme} toggleTheme={toggleTheme} accentColor={accentColor} onAccentChange={onAccentChange} />

      {/* Hero */}
      <section className="relative h-[65vh] flex items-center justify-center group">
        <div className="absolute inset-0 z-0" style={{ backgroundImage: `url(${content.hero?.backgroundImage || defaultHero.backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(2,30,15,0.75) 50%, rgba(0,10,5,0.7) 100%)' }}></div>
        </div>
        <SectionEditor user={user} sectionTitle="Hero" fields={[
          { key: 'title', label: 'Title', type: 'title', styled: true },
          { key: 'subtitle', label: 'Subtitle', type: 'text', styled: true },
          { key: 'backgroundImage', label: 'Background Image', type: 'image' },
        ]} content={content.hero} pageName="sustainability" sectionKey="hero" onSave={updateSection('hero')} />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tighter" style={applyStyle(content.hero?.titleStyle)} data-testid="sustainability-title">
            Commitment to
            <span className="block text-primary">{content.hero?.title || 'Sustainability'}</span>
          </h1>
          <p className="text-lg text-gray-200" style={applyStyle(content.hero?.subtitleStyle)}>{content.hero?.subtitle || 'Building a greener future through eco-friendly packaging solutions and responsible manufacturing practices'}</p>
        </div>
      </section>

      {/* Initiatives Grid */}
      <section className="py-24 relative section-fade-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge mb-4">OUR INITIATIVES</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">Environmental Impact</h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Measurable actions we're taking to reduce our environmental footprint</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {initiatives.map((initiative, index) => {
              const Icon = initiative.icon;
              return (
                <motion.div key={initiative.title || `initiative-${index}`} className="feature-card" data-testid={`initiative-card-${index}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <p className="text-3xl font-bold text-primary font-mono-spec">{initiative.stat}</p>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{initiative.title}</h3>
                  <p className="text-muted-foreground text-sm">{initiative.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6 tracking-tight">Our 2030 Commitments</h2>
              <p className="text-lg text-muted-foreground mb-8">We're committed to ambitious sustainability goals that will transform our industry.</p>
              <ul className="space-y-4">
                {commitments.map((commitment, index) => (
                  <li key={`commitment-${index}`} className="flex items-start" data-testid={`commitment-${index}`}>
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <svg className="w-4 h-4 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground/80">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden border border-border/40">
              <img src="https://images.unsplash.com/photo-1705837864006-221974a37ea3" alt="Sustainability" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'hsl(var(--primary))' }}></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-4 tracking-tight">Our Impact in Numbers</h2>
            <p className="text-lg text-primary-foreground/60">Making a real difference to our planet</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '50M+', label: 'Kg CO2 Saved Annually' },
              { value: '100K+', label: 'Trees Planted Since 2020' },
              { value: '75%', label: 'Waste Reduction Achieved' },
              { value: '30+', label: 'Green Partnerships' },
            ].map((stat, index) => (
              <motion.div key={stat.label || `impact-${index}`} className="text-center" data-testid={`impact-stat-${index}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }}>
                <p className="text-4xl lg:text-5xl font-bold text-primary-foreground mb-2 font-mono-spec">{stat.value}</p>
                <p className="text-sm text-primary-foreground/60">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer user={user} setUser={setUser} />
    </div>
  );
}
